import { motion } from 'framer-motion';
import { Divide, Minus, Plus, X as XIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { BottomSheet, NumPad } from '../../../components';
import { useAppData } from '../../../data';
import { cn } from '../../../lib/cn';
import { formatCurrency } from '../../../lib/currency';
import { springQuick, tapScale } from '../../../tokens';

type Op = '+' | '-' | '×' | '÷';
type Field = 'a' | 'b';

type Props = { open: boolean; onClose: () => void };

const OPS: Array<{ value: Op; icon: LucideIcon; label: string }> = [
  { value: '+', icon: Plus, label: 'Add' },
  { value: '-', icon: Minus, label: 'Subtract' },
  { value: '×', icon: XIcon, label: 'Multiply' },
  { value: '÷', icon: Divide, label: 'Divide' },
];

function compute(a: number, b: number, op: Op): number {
  switch (op) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '×':
      return a * b;
    case '÷':
      return b === 0 ? 0 : a / b;
  }
}

/**
 * Quick Math — two NumPad-driven operands with an operator selector
 * between them. The result auto-updates and renders large in the accent
 * color. Tapping an operand makes it active for the bottom NumPad; the
 * Done key swaps to the other operand.
 */
export function QuickMath({ open, onClose }: Props) {
  const data = useAppData();
  const currency = data.profile.currency;

  const [a, setA] = useState(0);
  const [b, setB] = useState(0);
  const [op, setOp] = useState<Op>('+');
  const [active, setActive] = useState<Field>('a');

  useEffect(() => {
    if (!open) return;
    setA(0);
    setB(0);
    setOp('+');
    setActive('a');
  }, [open]);

  const result = compute(a, b, op);
  const hasDivByZero = op === '÷' && b === 0;

  const advance = () => {
    setActive(active === 'a' ? 'b' : 'a');
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Quick math">
      <div className="px-card-pad pt-2 pb-3 flex flex-col gap-2">
        <Operand
          value={formatCurrency(a, { currency })}
          active={active === 'a'}
          onClick={() => setActive('a')}
        />

        <div className="grid grid-cols-4 gap-2">
          {OPS.map((o) => {
            const Icon = o.icon;
            const isActive = op === o.value;
            return (
              <motion.button
                key={o.value}
                type="button"
                onClick={() => setOp(o.value)}
                whileTap={tapScale}
                transition={springQuick}
                aria-label={o.label}
                aria-pressed={isActive}
                className={cn(
                  'h-11 rounded-button inline-flex items-center justify-center',
                  isActive
                    ? 'bg-accent text-white shadow-sm'
                    : 'bg-surface-secondary text-text-secondary',
                )}
              >
                <Icon size={18} strokeWidth={1.5} />
              </motion.button>
            );
          })}
        </div>

        <Operand
          value={formatCurrency(b, { currency })}
          active={active === 'b'}
          onClick={() => setActive('b')}
        />

        <div className="mt-1 flex items-baseline justify-between rounded-button bg-accent/[0.08] px-4 py-3">
          <span className="text-[13px] font-medium uppercase tracking-[0.05em] text-accent">
            =
          </span>
          <span className="text-[28px] font-light font-mono tabular-nums text-accent">
            {hasDivByZero
              ? '—'
              : formatCurrency(result, { currency })}
          </span>
        </div>
      </div>

      {active === 'a' ? (
        <NumPad
          key="a"
          initialValue={a}
          onChange={setA}
          onDone={advance}
          doneLabel="Next"
          caption={null}
        />
      ) : (
        <NumPad
          key="b"
          initialValue={b}
          onChange={setB}
          onDone={onClose}
          doneLabel="Done"
          caption={null}
        />
      )}
    </BottomSheet>
  );
}

function Operand({
  value,
  active,
  onClick,
}: {
  value: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      aria-pressed={active}
      className={cn(
        'w-full flex items-baseline justify-end rounded-button px-4 py-3 text-right min-h-tap',
        active ? 'bg-accent/10 text-accent' : 'bg-surface-secondary text-text-primary',
      )}
    >
      <span className="text-[20px] font-mono tabular-nums">{value}</span>
    </motion.button>
  );
}
